import {
  BFF_LOGOUT_PATH,
  BFF_USER_PATH,
  BffUserResponse,
  buildLoginUrl,
  currentReturnUrl,
  parseBffUser,
} from './bff'
import { CSRF_HEADER_NAME, XSRF_COOKIE_NAME, readCookie } from './bff'

/**
 * Authentication, as of the BFF (phase 9.6).
 *
 * This file used to be an OIDC client: it ran the authorization-code flow in the browser, kept the
 * resulting tokens in `sessionStorage`, renewed them on a timer, and handed an `Authorization` header to
 * every API call. None of that is here any more, and that is the entire point — there is no credential in
 * the browser to steal, and ending a session server-side makes the cookie useless on the next request.
 *
 * What replaces it is four URLs and a cookie the browser sends by itself. Signing in is a navigation, not
 * a library call; knowing who you are is a `GET /bff/user`; signing out is a `POST /bff/logout` that also
 * ends the session at the identity provider.
 *
 * The tenant is still not configured here. One issuer serves every tenant and the tenant travels with the
 * session, put there by the identity provider at sign-in. Where a sign-in needs to name a tenant up front
 * it comes from the `/t/{slug}` URL and rides along as a hint — see `loginToTenant`.
 */

/** What the app knows about the signed-in user. Unchanged from the token era; the source of it is not. */
export interface AuthAccount {
  /** The subject our IdP issued — half of the platform user key; the tenant is the other half. */
  readonly subject: string
  readonly displayName: string
  readonly email: string
  /** The tenant this session acts in. Exactly one, always (ADR 024 decision 1). */
  readonly tenantId: string | null
  readonly roles: readonly string[]
}

/** Projects the wire shape onto what components read. */
export function toAccount(user: BffUserResponse): AuthAccount {
  return {
    subject: user.subject,
    displayName: user.displayName ?? user.email ?? 'User',
    email: user.email ?? '',
    tenantId: user.tenantId,
    roles: user.roles,
  }
}

/**
 * Asks the BFF who this browser is signed in as.
 *
 * A 401 resolves to `null` — signed out is an *answer*, and the caller has to be able to tell it from a
 * request that failed. Anything else throws, including a body that does not parse: a malformed response
 * quietly coerced into a user is how a broken session renders as a working one.
 */
export async function fetchSession(): Promise<AuthAccount | null> {
  const response = await fetch(BFF_USER_PATH, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })

  if (response.status === 401) {
    return null
  }

  if (!response.ok) {
    throw new Error(`Could not read the session (HTTP ${response.status}).`)
  }

  return toAccount(parseBffUser(await response.json()))
}

/**
 * Starts an interactive sign-in by navigating to the BFF.
 *
 * A navigation rather than a fetch: what follows is the identity provider's own login page, and the
 * browser has to be there to see it. The current location rides along as `returnUrl` so the round trip
 * lands back where it started, hash route included.
 */
export function login(returnTo?: string): void {
  window.location.assign(buildLoginUrl(returnTo ?? currentReturnUrl()))
}

/**
 * Starts a sign-in scoped to one tenant, from the `/t/{slug}` entry point or the switcher.
 *
 * The slug is a *hint* about which directory to authenticate against, never an authorization decision: the
 * identity provider resolves it against the user's own memberships and refuses a tenant they do not belong
 * to.
 */
export function loginToTenant(slug: string, returnTo = '#/'): void {
  const target = returnTo.startsWith('#')
    ? `${window.location.origin}${window.location.pathname}${returnTo}`
    : returnTo
  window.location.assign(buildLoginUrl(target, slug))
}

/**
 * Ends the session — here, at the BFF, and at the identity provider.
 *
 * Throws when the BFF refuses, and deliberately does not navigate in that case. Navigating away from a
 * session that is still open is the lie: the cookie would still be live, the app would re-render signed
 * in, and the button would look inert.
 *
 * A 401 is *not* a failure. It means the BFF has no session for this caller, which is exactly the state
 * sign-out is asking for, and it keeps signing out idempotent.
 */
export async function logout(): Promise<void> {
  const csrf = readCookie(XSRF_COOKIE_NAME)
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (csrf !== null) {
    headers[CSRF_HEADER_NAME] = csrf
  }

  // `Accept: application/json` asks the BFF to describe where to go rather than to take us there. A
  // redirect to the identity provider is useless to `fetch` — it follows it opaquely, or stops at a
  // response whose `Location` the browser will not reveal — and either way the provider's own session
  // would quietly survive the sign-out.
  const response = await fetch(BFF_LOGOUT_PATH, { method: 'POST', credentials: 'include', headers })

  if (!response.ok && response.status !== 401) {
    throw new Error(`Sign out failed: the BFF answered ${response.status}.`)
  }

  // The BFF answers with the identity provider's end-session URL when there is one to visit, so the
  // provider's own session ends too. Without this half, returning to the app signs the user straight back
  // in and sign-out looks broken in the one way that matters.
  const endSession = await readEndSessionUrl(response)
  window.location.assign(endSession ?? '/')
}

async function readEndSessionUrl(response: Response): Promise<string | null> {
  if (response.status !== 200) {
    return null
  }
  try {
    const body: unknown = await response.json()
    if (typeof body === 'object' && body !== null) {
      const url = (body as Record<string, unknown>).endSessionUrl
      return typeof url === 'string' && url.length > 0 ? url : null
    }
  } catch {
    // A sign-out that succeeded without a body is still a sign-out.
  }
  return null
}
